import * as React from 'react'
import {
  Block,
  ComponentBlock,
  isListBlock,
  isInstanceBlock,
  PreviewLinkBlock,
  getPossibleVariables,
  getPreviewURL,
  Option,
  isVariantSetBlock,
  getApiNameAndSlug,
  LiteBlock,
  withUniqueApiNames,
} from '@basehub/data/replicache/index'
import { useRepoReplicache } from '../use-repo-replicache'
import { invariant } from '@epic-web/invariant'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { usePreviewToken } from './use-preview-token'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import {
  GetVariables,
  Variable,
} from '~/components/variables/single-line-input'
import { useOriginUrl } from './use-origin-url'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useTreeSlice } from '~/hooks/use-tree-slice'

// ----------------------------------------------------------------------------------------------------------
//                                                  🎣 Hook
// ----------------------------------------------------------------------------------------------------------

export interface UsePreviewLinkProps {
  blockId: Block['id']
  treeManager: TreeManager<Block, NonNullable<unknown>>
}

export function usePreviewLink<RefElement = HTMLInputElement>({
  blockId,
  treeManager,
}: UsePreviewLinkProps) {
  // get blocks necessary for the hook
  const result = useTreeSlice(treeManager, (t) => {
    const nodeBlock = t.getNode(blockId) as TreeNode<PreviewLinkBlock>
    const block = nodeBlock?.value
    const path = t.getPath(blockId, { required: true })

    // get parent block
    invariant(block, `Block ${blockId} is required`)
    invariant(nodeBlock.parentKey, `Block ${blockId} parentKey is required`)
    const parent = t.getNode(nodeBlock.parentKey)?.value
    invariant(parent, 'Parent is required')

    return { nodeBlock, block, parent, path }
  })

  const { nodeBlock, block, parent, path } = result ?? {}

  const templateNodeBlock: TreeNode<ComponentBlock> | undefined =
    React.useMemo(() => {
      if (!parent || !block) return undefined
      if (isListBlock(parent) && isInstanceBlock(block)) {
        return treeManager?.getNode(block.typeOptions.targetBlockId) as
          | TreeNode<ComponentBlock>
          | undefined
      }
    }, [block, treeManager, parent])

  const repoReplicacheData = useRepoReplicache().data
  const replicache = repoReplicacheData?.replicache

  const selectedVariants = useSelectedVariants({
    parentBlock: block,
    ignoreDefault: false,
  })

  const ORIGIN = useOriginUrl()

  // helpers
  const isInstanceOfListBlockSyncedWithRef = React.useCallback(
    (v: string | undefined | null) =>
      parent &&
      block &&
      isListBlock(parent) &&
      isInstanceBlock(block) &&
      templateNodeBlock &&
      (!block.previewUrl || v === templateNodeBlock?.value.previewUrl),
    [block, parent, templateNodeBlock]
  )

  const { onChange, value, ref } = useSyncedValue<RefElement>({
    setValue: async (v: string) => {
      if (!block) return
      if (isInstanceOfListBlockSyncedWithRef(v)) return

      await replicache?.mutate.updatePreviewUrl({
        blockId: block.id,
        previewUrl: v,
        isoNow: new Date().toISOString(),
        path: path ?? '',
      })
    },
    value:
      block && isInstanceOfListBlockSyncedWithRef(block.previewUrl)
        ? (templateNodeBlock?.value.previewUrl ?? '')
        : (block?.previewUrl ?? ''),
    rateLimitMs: 200,
  })

  const variantsOptions = useTreeSlice(
    treeManager,
    (tm) =>
      selectedVariants.map(({ setId, variantId }) => {
        const set = tm.getNode(setId, { required: true })
        invariant(isVariantSetBlock(set.value), 'Variant set is required')
        const { _apiName } = getApiNameAndSlug({
          nodeBlock: set,
          treeManager: {
            getNode: tm.getNode,
            getChildrenKeys: tm.getChildrenKeys,
          },
        })
        const variant = withUniqueApiNames(set.value.value.variants).find(
          (v) => v.id === variantId
        )
        invariant(variant, 'Variant is required')
        return {
          name: 'variant_' + _apiName!,
          value: variant.apiName,
          node: null,
        } satisfies Option
      }),
    'reference',
    [selectedVariants]
  )

  const rootOptions: Option[] = React.useMemo(() => {
    return [
      {
        name: '_origin',
        value: ORIGIN,
        node: null,
      },
      {
        name: '_vercel_draft',
        value: '__vercel_draft=1',
        node: null,
      },
      {
        name: '_root',
        value: 'Root Block',
        node: treeManager.root,
      },
      ...(variantsOptions ?? []),
    ]
  }, [ORIGIN, treeManager.root, variantsOptions])

  const getVariables: GetVariables = React.useCallback(
    (segments) => {
      if (!nodeBlock) return []
      return getPossibleVariables({
        segments,
        nodeBlock,
        treeManager: treeManager as TreeManager<LiteBlock>,
        valueKey: 'value',
        rootOptions,
        selectedVariants,
      })
        .filter((v) => !v.hidden)
        .map((v) => {
          return {
            info: v.value,
            label: v.name,
            displayLabel: v.displayLabel,
          } satisfies Variable
        })
    },
    [nodeBlock, treeManager, rootOptions, selectedVariants]
  )

  const previewTokenData = usePreviewToken().data

  const previewLink =
    useTreeSlice(
      treeManager,
      (tm) => {
        if (!tm) return ''
        if (!nodeBlock) return ''
        return getPreviewURL({
          nodeBlock,
          blockTreeManager: tm as TreeManager<LiteBlock>,
          previewToken: previewTokenData?.bshbPreview,
          previewRef: previewTokenData?.previewRef ?? undefined,
          rootOptions,
          origin: ORIGIN,
          selectedVariants,
        })
      },
      'reference',
      [nodeBlock, rootOptions, previewTokenData, ORIGIN, selectedVariants]
    ) ?? ''

  return {
    onChange,
    value,
    ref,
    block,
    templateBlock: templateNodeBlock?.value,
    getVariables,
    previewLink,
  }
}
